/**
 * AddSectionModal Component
 * Modal for adding new sections to templates
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';

/**
 * AddSectionModal component
 */
export function AddSectionModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  existingSections = [],
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    required: true,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Section title is required';
    } else if (existingSections.some(s => s.title.toLowerCase() === formData.title.toLowerCase())) {
      newErrors.title = 'A section with this title already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    onSubmit?.({
      title: formData.title.trim(),
      description: formData.description.trim(),
      required: formData.required,
    });

    // Reset form
    setFormData({
      title: '',
      description: '',
      required: true,
    });
    setErrors({});
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      required: true,
    });
    setErrors({});
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]" data-testid="add-section-modal">
        <DialogHeader>
          <DialogTitle>Add New Section</DialogTitle>
          <DialogDescription>
            Create a new section for the template. Sections define the structure of documents.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Section Title */}
            <div className="space-y-2">
              <Label htmlFor="section-title">Section Title *</Label>
              <Input
                id="section-title"
                placeholder="e.g., Executive Summary"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className={errors.title ? 'border-red-500' : ''}
                data-testid="section-title-input"
              />
              {errors.title && (
                <p className="text-xs text-red-500">{errors.title}</p>
              )}
            </div>

            {/* Section Description */}
            <div className="space-y-2">
              <Label htmlFor="section-description">Description</Label>
              <Textarea
                id="section-description"
                placeholder="Brief description of what this section should contain..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                data-testid="section-description-input"
              />
            </div>

            {/* Required Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="section-required">Required Section</Label>
                <p className="text-xs text-muted-foreground">
                  Required sections must be completed before submission
                </p>
              </div>
              <Switch
                id="section-required"
                checked={formData.required}
                onCheckedChange={(checked) => handleChange('required', checked)}
                data-testid="section-required-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              data-testid="add-section-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Section'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddSectionModal;
